import React, { useState } from 'react'
import { getDeepgramRequestDetails } from '../../repository/aiCostApi'
import { providerLabel, providerColor, usd, num, utcDateTime, duration } from './aiCostFormat'

const COLUMNS = [
  { key: 'date', label: 'Date (UTC)', sortable: true },
  { key: 'provider', label: 'Provider', sortable: true },
  { key: 'model', label: 'Model', sortable: true },
  { key: 'feature', label: 'Feature', sortable: true },
  { key: 'requestId', label: 'Request ID' },
  { key: 'audioDuration', label: 'Audio Duration', sortable: true, numeric: true },
  { key: 'tokens', label: 'Tokens', sortable: true, numeric: true },
  { key: 'cost', label: 'Cost', sortable: true, numeric: true },
]
const PAGE_SIZES = [10, 20, 50, 100]

/** Drill-down: live request details from Deepgram, fetched by the backend */
const RequestDetails = ({ row, onClose }) => {
  const [state, setState] = useState({ loading: true, data: null, error: null })

  React.useEffect(() => {
    let alive = true
    getDeepgramRequestDetails(row.requestId, row.project?._id)
      .then((res) => alive && setState({ loading: false, data: res.data, error: null }))
      .catch((err) => alive && setState({ loading: false, data: null, error: err.message }))
    return () => {
      alive = false
    }
  }, [row])

  return (
    <div className="ai-cost-modal-backdrop" onClick={onClose} role="presentation">
      <div className="ai-cost-modal" role="dialog" aria-modal="true" aria-label="Request details" onClick={(e) => e.stopPropagation()}>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <strong>Deepgram request {row.requestId}</strong>
          <button type="button" className="btn-close" aria-label="Close" onClick={onClose} />
        </div>
        {state.loading && <div className="ai-cost-state">Loading...</div>}
        {state.error && <div className="text-danger">{state.error}</div>}
        {state.data && <pre className="ai-cost-json">{JSON.stringify(state.data, null, 2)}</pre>}
      </div>
    </div>
  )
}

/**
 * Request log table. Sorting and paging are server side:
 * sort = { sortBy, sortOrder }, page starts at 1.
 */
const AIUsageTable = ({ data, isLoading, error, sort, onSort, page, limit, onPage, onLimit }) => {
  const [details, setDetails] = useState(null)
  const rows = data?.rows || []
  const totalPages = data?.totalPages || 0

  const headerClick = (col) => {
    if (!col.sortable) return
    onSort(sort.sortBy === col.key ? { sortBy: col.key, sortOrder: sort.sortOrder === 'desc' ? 'asc' : 'desc' } : { sortBy: col.key, sortOrder: 'desc' })
  }

  return (
    <div className="ai-cost-card">
      <div className="ai-cost-card-head">
        <div className="ai-cost-card-title">Requests</div>
        <div className="ai-cost-card-subtitle">{data ? `${num(data.totalCount)} requests` : ''}</div>
      </div>

      {error ? (
        <div className="ai-cost-state text-danger">{error.message}</div>
      ) : (
        <>
          <div className="table-responsive">
            <table className="table table-sm ai-cost-table">
              <thead>
                <tr>
                  {COLUMNS.map((col) => {
                    const active = sort.sortBy === col.key
                    return (
                      <th
                        key={col.key}
                        className={`${col.numeric ? 'text-end' : ''} ${col.sortable ? 'ai-cost-sortable' : ''}`}
                        onClick={() => headerClick(col)}
                        aria-sort={active ? (sort.sortOrder === 'asc' ? 'ascending' : 'descending') : undefined}>
                        {col.label}
                        {col.sortable && <span className="ai-cost-sort">{active ? (sort.sortOrder === 'asc' ? ' ▲' : ' ▼') : ' ↕'}</span>}
                      </th>
                    )
                  })}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r._id} className={r.status === 'failed' ? 'ai-cost-row-failed' : ''}>
                    <td>{utcDateTime(r.date)}</td>
                    <td>
                      <span className="ai-cost-swatch me-1" style={{ background: providerColor(r.provider) }} />
                      {providerLabel(r.provider)}
                    </td>
                    <td>{r.model}</td>
                    <td>{r.feature}</td>
                    <td className="ai-cost-mono">
                      {r.requestId && r.provider === 'deepgram' ? (
                        <button type="button" className="btn btn-link btn-sm p-0 ai-cost-mono" onClick={() => setDetails(r)}>
                          {r.requestId.slice(0, 13)}…
                        </button>
                      ) : r.requestId ? (
                        <span title={r.requestId}>{r.requestId.slice(0, 13)}…</span>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="text-end">{duration(r.audioSeconds)}</td>
                    <td
                      className="text-end"
                      title={r.totalTokens ? `Input ${num(r.inputTokens)} · Output ${num(r.outputTokens)} · Thinking ${num(r.thinkingTokens)}` : undefined}>
                      {r.totalTokens ? num(r.totalTokens) : '-'}
                    </td>
                    <td className="text-end" title={r.costSource === 'provider' ? 'Actual cost from provider' : r.costSource === 'unpriced' ? 'No price configured' : 'Estimated'}>
                      {usd(r.cost)}
                      {r.costSource === 'estimated' && <span className="text-muted"> *</span>}
                    </td>
                  </tr>
                ))}
                {!isLoading && !rows.length && (
                  <tr>
                    <td colSpan={COLUMNS.length} className="text-center text-muted py-4">
                      No requests match these filters
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="ai-cost-pager">
            <span className="text-muted small">* estimated: Deepgram per-minute price, or Gemini tokens × Gemini Pricing</span>
            <div className="d-flex align-items-center gap-2">
              <select className="form-select form-select-sm w-auto" aria-label="Rows per page" value={limit} onChange={(e) => onLimit(Number(e.target.value))}>
                {PAGE_SIZES.map((n) => (
                  <option key={n} value={n}>
                    {n} / page
                  </option>
                ))}
              </select>
              <span className="small text-muted">
                Page {totalPages ? page : 0} of {totalPages}
              </span>
              <button type="button" className="btn btn-sm btn-outline-secondary" disabled={page <= 1} onClick={() => onPage(page - 1)}>
                Previous
              </button>
              <button type="button" className="btn btn-sm btn-outline-secondary" disabled={page >= totalPages} onClick={() => onPage(page + 1)}>
                Next
              </button>
            </div>
          </div>
        </>
      )}

      {details && <RequestDetails row={details} onClose={() => setDetails(null)} />}
    </div>
  )
}

export default AIUsageTable
